import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './useAuth';
import { invalidateEntitlements } from './useEntitlements';
import { apiRequest, resolveApiBaseUrl } from '../utils/apiClient';

const API_BASE = resolveApiBaseUrl();
const SUBSCRIPTION_STORAGE_KEY = 'safaqat24.subscription';

type UserCategory = 'company' | 'broker' | 'owner';
type ListingType = 'rent' | 'sale';
type BillingCycle = 'monthly' | 'yearly';

export interface Subscription {
  id?: string;
  userId: string;
  planId?: string;
  categoryType: UserCategory;
  listingType: ListingType;
  billingCycle: BillingCycle;
  status?: string;
  subscribedAt: string;
  expiresAt?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  maxListings: number;
  maxSubUsers: number;
  listingsUsed: number;
}

interface SubscriptionContextType {
  subscription: Subscription | null;
  isSubscribed: boolean;
  loading: boolean;
  error: string | null;
  listingsRemaining: number;
  daysRemaining: number;
  timeRemainingText: string;
  canCreateListing: boolean;
  // True when the active subscription belongs to the user's company admin,
  // not the user themselves. Team members on a subscribed company see this.
  isInherited: boolean;
  // True when the user is on a company team that has no active subscription.
  // UI shows "contact your company admin" instead of "subscribe yourself".
  needsContactCompanyAdmin: boolean;
  // companyId of the user's team (if any) — null for solo users.
  companyId: string | null;
  // Authoritative count of personal listings the user owns (Property where
  // ownerId = userId). Used to decide whether to render the lock screen.
  personalListingsCount: number;
  // The membership page calls this with `{ planId, billingCycle }` because
  // plan selection is now plan-driven (not category-driven). Both shapes are
  // accepted — the legacy category-based form is still used in tests.
  subscribe: (data: {
    categoryType?: UserCategory;
    listingType?: ListingType;
    billingCycle: BillingCycle;
    planId?: string;
  }) => Promise<void>;
  incrementListingsUsed: () => void;
  clearSubscription: () => void;
  refreshSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

const categoryLimits: Record<UserCategory, { maxListings: number; maxSubUsers: number }> = {
  company: { maxListings: 250, maxSubUsers: 4 },
  broker: { maxListings: 50, maxSubUsers: 0 },
  owner: { maxListings: 50, maxSubUsers: 0 },
};

function readStoredSubscription(userId: string): Subscription | null {
  try {
    const stored = localStorage.getItem(`${SUBSCRIPTION_STORAGE_KEY}.${userId}`);
    if (!stored) return null;
    return JSON.parse(stored) as Subscription;
  } catch {
    return null;
  }
}

function persistSubscription(userId: string, subscription: Subscription | null): void {
  if (subscription) {
    localStorage.setItem(`${SUBSCRIPTION_STORAGE_KEY}.${userId}`, JSON.stringify(subscription));
  } else {
    localStorage.removeItem(`${SUBSCRIPTION_STORAGE_KEY}.${userId}`);
  }
}

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user, session } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The subscription.listingsUsed field returned by /subscriptions/my is never
  // incremented at property-create time and is therefore unreliable. We pull
  // the real count from the properties API and use it everywhere the UI
  // displays a "remaining listings" number.
  const [realListingsUsed, setRealListingsUsed] = useState<number | null>(null);
  // Inheritance flags from /subscriptions/my - see backend
  // resolveEffectiveSubscription helper.
  const [isInherited, setIsInherited] = useState(false);
  const [needsContactCompanyAdmin, setNeedsContactCompanyAdmin] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);

  // Fetch subscription from API
  const fetchSubscription = useCallback(async () => {
    if (!user?.id || !session?.token) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/subscriptions/my`, {
        headers: {
          'Authorization': `Bearer ${session.token}`,
        },
      });

      if (response.ok) {
        const payload = await response.json();
        const data = payload?.data;
        setIsInherited(Boolean(payload?.isInherited));
        setNeedsContactCompanyAdmin(Boolean(payload?.needsContactCompanyAdmin));
        setCompanyId(payload?.companyId ?? null);
        if (data) {
          // Map API response to our subscription format. The plan's
          // maxListings/maxSubUsers live on the plan row itself, not under
          // a nested `features` object — the old fallback path was always
          // hitting the default 50.
          const sub: Subscription = {
            id: data.id,
            userId: user.id,
            planId: data.planId,
            categoryType: data.plan?.planType || 'owner',
            listingType: 'rent',
            billingCycle: data.billingCycle || 'monthly',
            status: data.status,
            subscribedAt: data.createdAt,
            expiresAt: data.currentPeriodEnd,
            currentPeriodStart: data.currentPeriodStart,
            currentPeriodEnd: data.currentPeriodEnd,
            maxListings: data.plan?.maxListings ?? data.plan?.features?.maxListings ?? 50,
            maxSubUsers: data.plan?.maxSubUsers ?? data.plan?.features?.maxSubUsers ?? 0,
            listingsUsed: data.usage?.listingsUsed || 0,
          };
          setSubscription(sub);
          persistSubscription(user.id, sub);
        } else {
          // No effective sub — clear local cache too so the lock screen
          // doesn't get bypassed by stale storage.
          setSubscription(null);
          persistSubscription(user.id, null);
        }
      } else {
        const stored = readStoredSubscription(user.id);
        setSubscription(stored);
      }
    } catch (err) {
      console.warn('Failed to fetch subscription from API, using local storage', err);
      // Fall back to local storage
      const stored = readStoredSubscription(user.id);
      setSubscription(stored);
    } finally {
      setLoading(false);
    }
  }, [user?.id, session?.token]);

  // Initial load
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const subscribe = useCallback(async (data: {
    categoryType?: UserCategory;
    listingType?: ListingType;
    billingCycle: BillingCycle;
    planId?: string;
  }) => {
    if (!user?.id) return;
    setError(null);

    // Plan-driven storefront flow: create the subscription on the BACKEND so it
    // is authoritative — it grants the plan's included coins, enforces listing
    // limits, and unblocks listing creation. Then refetch the real state. (Once
    // Moyasar is configured the backend rejects this with PAYMENT_REQUIRED, which
    // throws here so the caller can route to checkout instead of faking a sub.)
    if (data.planId) {
      if (!session?.token) throw new Error('Not authenticated.');
      await apiRequest('/subscriptions/subscribe', {
        method: 'POST',
        token: session.token,
        body: { planId: data.planId, billingCycle: data.billingCycle },
      });
      // A qualifying plan unlocks «مؤشرات السوق»: drop the cached access answer
      // (client store and, for the next minute, the server's 60 s cache).
      invalidateEntitlements();
      await fetchSubscription();
      return;
    }

    // Legacy category-based path (no planId — used by tests): optimistic local sub.
    const limits = data.categoryType
      ? categoryLimits[data.categoryType]
      : categoryLimits.owner;
    const now = new Date();
    const expiresAt = new Date(now);
    if (data.billingCycle === 'monthly') {
      expiresAt.setMonth(expiresAt.getMonth() + 1);
    } else {
      expiresAt.setFullYear(expiresAt.getFullYear() + 1);
    }
    const newSubscription: Subscription = {
      userId: user.id,
      planId: data.planId,
      categoryType: data.categoryType ?? 'owner',
      listingType: data.listingType ?? 'rent',
      billingCycle: data.billingCycle,
      status: 'ACTIVE',
      subscribedAt: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
      currentPeriodStart: now.toISOString(),
      currentPeriodEnd: expiresAt.toISOString(),
      maxListings: limits.maxListings,
      maxSubUsers: limits.maxSubUsers,
      listingsUsed: 0,
    };
    setSubscription(newSubscription);
    persistSubscription(user.id, newSubscription);
  }, [user?.id, session?.token, fetchSubscription]);

  const clearSubscription = useCallback(() => {
    if (user?.id) {
      setSubscription(null);
      persistSubscription(user.id, null);
    }
  }, [user?.id]);

  const refreshSubscription = useCallback(async () => {
    setLoading(true);
    await fetchSubscription();
  }, [fetchSubscription]);

  const incrementListingsUsed = useCallback(() => {
    if (!user?.id || !subscription) return;

    const updated: Subscription = {
      ...subscription,
      listingsUsed: subscription.listingsUsed + 1,
    };
    setSubscription(updated);
    persistSubscription(user.id, updated);
    // Also bump the authoritative real count so the UI updates immediately
    // after creating a listing without waiting for the next refetch.
    setRealListingsUsed((current) => (current === null ? null : current + 1));
  }, [user?.id, subscription]);

  const isSubscribed = useMemo(() => {
    if (!subscription) return false;

    // Check if subscription is cancelled
    if (subscription.status === 'CANCELLED' || subscription.status === 'EXPIRED') {
      return false;
    }

    // Check if subscription is expired
    const endDate = subscription.expiresAt || subscription.currentPeriodEnd;
    if (endDate) {
      const expiresAt = new Date(endDate);
      if (expiresAt < new Date()) {
        return false;
      }
    }

    return true;
  }, [subscription]);

  // Fetch the real listings count from the property API (authoritative —
  // counts rows in Property table by ownerId). subscription.listingsUsed is
  // a stale field that is never incremented server-side.
  useEffect(() => {
    if (!session?.token) {
      setRealListingsUsed(null);
      return;
    }
    let cancelled = false;
    apiRequest<{ meta?: { totalItems?: number } }>('/properties?mine=true&pageSize=1', {
      token: session.token,
    })
      .then((res) => {
        if (!cancelled) {
          setRealListingsUsed(res.meta?.totalItems ?? 0);
        }
      })
      .catch((err) => {
        console.warn('useSubscription: failed to fetch real listings count', err);
      });
    return () => {
      cancelled = true;
    };
  }, [session?.token]);

  // Calculate listings remaining — prefer the real count, fall back to the
  // stale subscription field if the real count hasn't loaded yet.
  const listingsRemaining = useMemo(() => {
    if (!subscription) return 0;
    const used = realListingsUsed ?? subscription.listingsUsed;
    return Math.max(0, subscription.maxListings - used);
  }, [subscription, realListingsUsed]);

  // Calculate days remaining
  const daysRemaining = useMemo(() => {
    if (!subscription) return 0;
    const endDate = subscription.expiresAt || subscription.currentPeriodEnd;
    if (!endDate) return 0;
    const now = new Date();
    const expires = new Date(endDate);
    const diffMs = expires.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  }, [subscription]);

  // Format time remaining as text
  const { i18n: i18nForTime } = useTranslation();
  const currentLang = i18nForTime.language;
  const timeRemainingText = useMemo(() => {
    const isAr = currentLang?.startsWith('ar');
    if (daysRemaining <= 0) return isAr ? 'منتهي' : 'Expired';
    if (daysRemaining === 1) return isAr ? 'يوم واحد متبقي' : '1 day remaining';
    if (daysRemaining < 30) return isAr ? `${daysRemaining} يوم متبقي` : `${daysRemaining} days remaining`;
    const months = Math.floor(daysRemaining / 30);
    if (months === 1) return isAr ? 'شهر واحد متبقي' : '1 month remaining';
    return isAr ? `${months} أشهر متبقية` : `${months} months remaining`;
  }, [daysRemaining, currentLang]);

  // Check if user can create a listing
  const canCreateListing = useMemo(() => {
    return isSubscribed && listingsRemaining > 0;
  }, [isSubscribed, listingsRemaining]);

  const personalListingsCount = realListingsUsed ?? 0;

  const value = useMemo<SubscriptionContextType>(
    () => ({
      subscription,
      isSubscribed,
      loading,
      error,
      listingsRemaining,
      daysRemaining,
      timeRemainingText,
      canCreateListing,
      isInherited,
      needsContactCompanyAdmin,
      companyId,
      personalListingsCount,
      subscribe,
      incrementListingsUsed,
      clearSubscription,
      refreshSubscription,
    }),
    [subscription, isSubscribed, loading, error, listingsRemaining, daysRemaining, timeRemainingText, canCreateListing, isInherited, needsContactCompanyAdmin, companyId, personalListingsCount, subscribe, incrementListingsUsed, clearSubscription, refreshSubscription]
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription(): SubscriptionContextType {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
